import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef, FavoriteEntity } from '@backstage/plugin-catalog-react';
import { ItemCardHeader, MarkdownContent } from '@backstage/core-components';
import { usePermission } from '@backstage/plugin-permission-react';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';
import type { TemplateCardProps } from '@backstage/plugin-scaffolder-react/alpha';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { TemplateCardTags } from './TemplateCardTags';
import { ViewButton, LaunchButton } from './Buttons';

interface HeaderStyleProps {
  backgroundImage: string;
  fontColor: string;
}

const useStyles = makeStyles<Theme, HeaderStyleProps>(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    backgroundImage: ({ backgroundImage }) => backgroundImage,
    color: ({ fontColor }) => fontColor,
  },
  actionContainer: {
    padding: theme.spacing(2),
    flex: 1,
    alignItems: 'flex-end',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    flex: 1,
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  box: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 10,
    '-webkit-box-orient': 'vertical' as const,
  },
  markdown: {
    '& :first-child': {
      margin: 0,
    },
  },
  subtitleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  favoriteIcon: {
    padding: 0,
    marginLeft: 6,
  },
}));

export function CustomTemplateCard({ template, onSelected }: TemplateCardProps) {
  const theme = useTheme<Theme>();
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const { allowed: canCreateTask } = usePermission({
    permission: taskCreatePermission,
  });

  const { title, name, namespace, description, tags } = template.metadata;
  const entityLink = catalogEntityRoute({
    kind: template.kind,
    namespace: namespace ?? 'default',
    name: name,
  });

  const type = template.spec.type;
  const hasTags = !!tags?.length;

  const pageTheme = theme.getPageTheme({ themeId: type });
  const classes = useStyles({
    backgroundImage: pageTheme.backgroundImage,
    fontColor: pageTheme.fontColor,
  });

  const subtitle = (
    <div className={classes.subtitleWrapper}>
      <div>{type}</div>
      <FavoriteEntity entity={template} className={classes.favoriteIcon} />
    </div>
  );

  return (
    <Card className={classes.card}>
      <ItemCardHeader
        title={title ?? name}
        subtitle={subtitle}
        classes={{ root: classes.header }}
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className={classes.box}>
              <MarkdownContent
                className={classes.markdown}
                content={description ?? 'No description'}
              />
            </Box>
          </Grid>
          {hasTags ? (
            <TemplateCardTags tags={tags} />
          ) : (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions className={classes.actionContainer}>
        <div className={classes.footer}>
          <ViewButton entityLink={entityLink} />
          {canCreateTask && <LaunchButton template={template} onSelected={onSelected} />}
        </div>
      </CardActions>
    </Card>
  );
}
